import { Flex, Text } from '@lego/klik-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader } from '@/components/common/Loader';
import { createColumnHelper } from '@/components/common/table/appTable';
import { TableStoreProvider } from '@/components/common/table/store/Context';
import { Table } from '@/components/common/table/Table';
import { useApiClient, useCallApi } from '@/hooks/api';
import { useDocumentTitle } from '@/hooks/common';
import type {
  WorkspaceListResponse,
  WorkspaceResponse,
} from '@/hooks/workspace/workspaces_resource';
import { useRegisterPageContext } from '@/providers/PageContextProvider';

const PAGE_SIZE = 500;

type LicenseRow = {
  workspaceName: string;
  activeEmmaOsLicenses: number;
};

const columnHelper = createColumnHelper<LicenseRow>();
const columns = columnHelper.columns([
  columnHelper.accessor('workspaceName', { header: 'Workspace' }),
  columnHelper.accessor('activeEmmaOsLicenses', { header: 'Active EMMA OS Licenses' }),
]);

const useLicenseData = () => {
  const callApi = useCallApi();
  const client = useApiClient();
  const [rows, setRows] = useState<LicenseRow[]>([]);
  const [totalLicenses, setTotalLicenses] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const hasFetched = useRef(false);

  const fetchAll = useCallback(async () => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    try {
      setIsLoading(true);
      setError(null);

      const fetchAllWorkspaces = async () => {
        const allWorkspaces: WorkspaceResponse[] = [];
        let offset = 0;
        let total = 0;
        do {
          const response = await client.v2.workspaces.$get({
            query: { limit: PAGE_SIZE.toString(), offset: offset.toString() },
          });
          if (response.status !== 200) {
            throw new Error(`Failed to load workspaces: ${response.status}`);
          }
          const result: WorkspaceListResponse = await response.json();
          allWorkspaces.push(...result.workspaces);
          total = result.itemCount;
          offset += PAGE_SIZE;
        } while (offset < total);
        return allWorkspaces;
      };

      const fetchDeviceCountPerWorkspace = async (workspaces: WorkspaceResponse[]) => {
        const counts = await Promise.all(
          workspaces.map(async (workspace) => {
            const result = await callApi((api) =>
              api.devices.getV5Devices({
                osNameLike: 'emmaOS',
                stateIn: ['PROVISIONED'],
                workspaceIdIn: [workspace.id],
                limit: 1,
                offset: 0,
              })
            );
            return { workspaceId: workspace.id, count: result.data.itemCount };
          })
        );
        return new Map(counts.map((c) => [c.workspaceId, c.count]));
      };

      const allWorkspaces = await fetchAllWorkspaces();
      const countByWorkspace = await fetchDeviceCountPerWorkspace(allWorkspaces);

      const licenseRows = allWorkspaces
        .map((ws) => ({
          workspaceName: ws.name,
          activeEmmaOsLicenses: countByWorkspace.get(ws.id) ?? 0,
        }))
        .filter((row) => row.activeEmmaOsLicenses > 0)
        .sort((a, b) => b.activeEmmaOsLicenses - a.activeEmmaOsLicenses);

      const total = licenseRows.reduce((sum, row) => sum + row.activeEmmaOsLicenses, 0);

      setRows(licenseRows);
      setTotalLicenses(total);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load data'));
    } finally {
      setIsLoading(false);
    }
  }, [callApi, client]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  return { rows, totalLicenses, isLoading, error };
};

export const EmmaOsLicensesPage = () => {
  useDocumentTitle('EMMA OS Licenses');

  const { rows, totalLicenses, isLoading, error } = useLicenseData();

  const pageContext = useMemo(
    () => (rows.length > 0 ? { totalLicenses, licenses: rows } : undefined),
    [rows, totalLicenses]
  );
  useRegisterPageContext(pageContext);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Flex alignItems="center" justifyContent="center" minHeight="200px">
        <Text color="red.500">Failed to load data.</Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="4">
      <Text>
        Total active EMMA OS licenses: <strong>{totalLicenses}</strong>
      </Text>
      <TableStoreProvider
        initialSorting={[{ desc: true, id: 'activeEmmaOsLicenses' }]}
        name="EmmaOsLicenses"
      >
        <Table columns={columns} data={rows} title="EMMA OS Licenses" />
      </TableStoreProvider>
    </Flex>
  );
};
